import { ReactNode, useMemo, useRef, useState } from 'react';
import draftToHtml from 'draftjs-to-html';

import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { mergeLessonAssessment } from 'approot/shared/logic/merge-lesson-assessment';
import {
  getLessonMaterialsPreview,
  getMaterialLabel,
  hasLessonMaterials,
} from './lesson-material.utils';
import {
  ActivityMaterials,
  Materials,
  MaterialsData,
} from './lesson-materials.constants';
import * as Styled from './lesson-material.style';
import { convertRichtextToPlainText } from 'lib/form/richtextarea/richtextarea.utils';
import { ReadMoreWrapper } from 'approot/shared/read-more/read-more.style';
import { LinkButton } from 'approot/shared/styles/buttons/link-button.style';
import LessonMaterialsList from 'approot/shared/lesson-materials/lesson-materials-list';
import { LessonMaterialFragment } from 'approot/shared/lesson-materials/__generated__/LessonMaterialFragment';
import { Heading16 } from 'approot/shared/styles/typography.style';
import LessonMaterialModal from './lesson-material-modal';

export const LessonMaterial = ({
  lessonMaterials,
  render,
}: {
  lessonMaterials?: MaterialsData;
  render: (content: ReactNode) => ReactNode;
}) => {
  const [isPreview, setIsPreview] = useState<boolean>(true);

  const hasMoreText = useMemo(() => {
    let total = '';
    lessonMaterials?.materials?.forEach(content => {
      total += convertRichtextToPlainText(content.data.richtext);
    });
    return total.length > 276;
  }, [lessonMaterials]);

  const textContent = lessonMaterials?.materials?.map((material, index) => (
    <Styled.LessonMaterialItem
      key={index}
      dangerouslySetInnerHTML={{
        __html: draftToHtml(material.data.richtext),
      }}
    />
  ));

  if (!lessonMaterials || !hasLessonMaterials(lessonMaterials)) return null;

  return (
    <>
      {render(
        <>
          {hasMoreText ? (
            <ReadMoreWrapper isPreview={isPreview}>
              {textContent}
            </ReadMoreWrapper>
          ) : (
            textContent
          )}
          {hasMoreText && (
            <LinkButton
              type="button"
              aria-label={`Read more about`}
              onClick={() => setIsPreview(!isPreview)}
            >
              Read {isPreview ? 'More' : 'Less'}
            </LinkButton>
          )}
        </>
      )}
    </>
  );
};

export const LessonMaterialActivities = ({
  notes,
  render,
  withReadMore = true,
}: {
  notes: ActivityMaterials[];
  render: (content: ReactNode) => ReactNode;
  withReadMore?: boolean;
}) => {
  const [isPreview, setIsPreview] = useState<boolean>(true);

  const filteredMaterials = useMemo(
    () =>
      (notes
        .flatMap(n => n.lessonMaterials)
        .filter(m => m && !!m.name.trim()) as LessonMaterialFragment[]) || [],
    [notes]
  );

  const hasMoreText = filteredMaterials.length > 5 && withReadMore;

  const textContent = notes.map(note => (
    <div key={note.id}>
      <Heading16 as="h3" marginBottom={8}>
        Activity {(note.component?.index || 0) + 1}
      </Heading16>
      <LessonMaterialsList list={note.lessonMaterials} />
    </div>
  ));

  return filteredMaterials.length ? (
    <>
      {render(
        <>
          {hasMoreText ? (
            <ReadMoreWrapper isPreview={isPreview}>
              {textContent}
            </ReadMoreWrapper>
          ) : (
            textContent
          )}
          {hasMoreText && (
            <LinkButton
              type="button"
              aria-label={`Read more about`}
              onClick={() => setIsPreview(!isPreview)}
            >
              Read {isPreview ? 'More' : 'Less'}
            </LinkButton>
          )}
        </>
      )}
    </>
  ) : null;
};

export const LessonMaterialSummary = ({
  lessons,
  assessments,
  isIntegrated,
  render,
}: {
  lessons?: MaterialsData[];
  assessments?: MaterialsData[];
  isIntegrated: boolean;
  render: (content: ReactNode) => ReactNode;
}) => {
  const selectedRef = useRef<Materials | null>(null);
  const { isVisible, closeModal, openModal } = useSimpleModalState();

  const { materials } = useMemo(() => {
    const merged = mergeLessonAssessment(
      lessons || [],
      assessments || [],
      isIntegrated
    );
    return getLessonMaterialsPreview(merged, {
      isIntegrated,
    });
  }, [assessments, lessons, isIntegrated]);

  const isMaterialsEmpty = useMemo(() => materials.length === 0, [materials]);

  const allHaveActivitiesMaterials = useMemo(() => {
    return (
      materials.length > 0 &&
      materials.every(item => item.activitiesMaterials?.length)
    );
  }, [materials]);

  if (isMaterialsEmpty || allHaveActivitiesMaterials) {
    return null;
  }

  return (
    <>
      {render(
        <>
          {isVisible && selectedRef.current && (
            <LessonMaterialModal
              item={selectedRef.current}
              onClose={closeModal}
            />
          )}
          <Styled.LessonMaterialButtons>
            {materials.map((item, index) => {
              if (item.activitiesMaterials?.length) {
                return null;
              }

              return (
                <Styled.LessonMaterialButton
                  type="button"
                  key={index}
                  aria-haspopup="dialog"
                  aria-label={`View ${getMaterialLabel(item)} Materials`}
                  onClick={() => {
                    selectedRef.current = item;
                    openModal();
                  }}
                >
                  {getMaterialLabel(item)}
                </Styled.LessonMaterialButton>
              );
            })}
          </Styled.LessonMaterialButtons>
        </>
      )}
    </>
  );
};
